import React, { useState, useEffect } from "react";
import {
  Box,
  Divider,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import MoneyIcon from "@mui/icons-material/Money";
import { format } from "date-fns";

import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import useIsMobile from "../../shared_modules/useIsMobile";
import { useTenant } from "../../hooks/useTenant";
import { apiRequest } from "../../api/api";
import { getSubdomain } from "../../utils/getSubdomain";
import { formatDateTime, formatTime } from "../../utils/functions";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "15px",
      color: "#111008",
      fontFamily: "inherit",
      "::placeholder": { color: "#b0a898" },
    },
    invalid: { color: "#e53935" },
  },
};

function calcFees(servicePrice, tipAmount = 0, feeRate = 0.025) {
  const p = Number(servicePrice);
  const tip = Number(tipAmount) || 0;
  const platformFee = Math.round(p * feeRate * 100) / 100;
  const subtotal = p + tip + platformFee;
  const totalCharge = Math.round(((subtotal + 0.3) / 0.971) * 100) / 100;
  const processingFee = Math.round((totalCharge - p - tip) * 100) / 100;
  return { servicePrice: p, tipAmount: tip, processingFee, totalCharge };
}

const TIP_PRESETS = ["none", "2", "5", "10", "custom"];

const PaymentForm = ({
  barber,
  service,
  slot,
  guestInfo,
  onSuccess,
  paymentMethod,
  onPaymentMethodChange,
  onTipAmountChange,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const isMobile = useIsMobile();
  const { tenant } = useTenant();
  const domain = getSubdomain();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [notes, setNotes] = useState("");
  const [tipAmount, setTipAmount] = useState(0);
  const [tipPreset, setTipPreset] = useState("none");
  const [customTip, setCustomTip] = useState("");

  // Reset tip when switching to cash
  useEffect(() => {
    if (paymentMethod === "cash") {
      setTipAmount(0);
      setTipPreset("none");
      setCustomTip("");
    }
  }, [paymentMethod]);

  // Notify parent of tip changes
  useEffect(() => {
    onTipAmountChange?.(paymentMethod === "card" ? tipAmount : 0);
  }, [tipAmount, paymentMethod]);

  const { servicePrice, processingFee, totalCharge } = calcFees(
    service?.price || 0,
    paymentMethod === "card" ? tipAmount : 0,
    tenant?.processing_fee_rate ?? 0.025,
  );

  const cancelWindowHours = tenant?.cancellation_window_hours ?? 24;

  const startsAt = slot?.start_utc
    ? new Date(slot.start_utc.replace(" ", "T") + "Z")
    : null;

  const cancelDeadline = startsAt
    ? new Date(startsAt.getTime() - cancelWindowHours * 60 * 60 * 1000)
    : null;

  const handleTipPreset = (preset) => {
    setTipPreset(preset);
    if (preset === "none") {
      setTipAmount(0);
      setCustomTip("");
    } else if (preset !== "custom") {
      setTipAmount(parseFloat(preset));
      setCustomTip("");
    }
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, paymentMethod: pm } =
        await stripe.createPaymentMethod({ type: "card", card: cardElement });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      const payload = {
        domain,
        first_name: guestInfo.firstName,
        last_name: guestInfo.lastName,
        email: guestInfo.email.trim().toLowerCase(),
        phone: guestInfo.phone,
        barber_user_id: barber.user_id,
        service_id: service.service_id,
        starts_at: slot.start_utc,
        payment_method_id: pm.id,
        notes: notes.trim() || null,
        tip_amount: paymentMethod === "card" ? tipAmount : 0,
      };

      const endpoint =
        paymentMethod === "card"
          ? "/guests/create-payment-intent.php"
          : "/guests/create-setup-intent.php";

      const res = await apiRequest(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      onSuccess(res.data);
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const tipButtonSx = (preset) => ({
    borderRadius: "8px",
    textTransform: "none",
    minWidth: "unset",
    px: 2,
    py: 0.75,
    fontSize: "0.875rem",
    fontWeight: 600,
    border: "1px solid",
    ...(tipPreset === preset
      ? {
          backgroundColor: "#111008",
          color: "white",
          borderColor: "#111008",
          "&:hover": { backgroundColor: "#1e1c14", borderColor: "#1e1c14" },
        }
      : {
          backgroundColor: "#fafaf8",
          color: "text.primary",
          borderColor: "#d8d1c7",
          "&:hover": { backgroundColor: "#f0ede6", borderColor: "#b0a898" },
        }),
  });

  return (
    <Box sx={{ mt: 4 }}>
      {/* Booking summary card */}
      <Box className="calendar-shell" sx={{ backgroundColor: "white" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              width: "4.5rem",
              height: "4.5rem",
              borderRadius: "50%",
              backgroundColor: "#f0ede6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.4rem",
              fontWeight: 700,
              color: "#C4984D",
              flexShrink: 0,
              overflow: "hidden",
            }}
          >
            {barber?.avatar_url ? (
              <Box
                component="img"
                src={barber.avatar_url}
                alt={`${barber.first_name} ${barber.last_name}`}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <>
                {barber?.first_name?.[0]}
                {barber?.last_name?.[0]}
              </>
            )}
          </Box>
          <Box sx={{ ml: 2 }}>
            <Typography variant="h6">
              {barber?.first_name} {barber?.last_name}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
              <StarIcon sx={{ color: "secondary.main", fontSize: "0.9rem" }} />
              <StarIcon sx={{ color: "secondary.main", fontSize: "0.9rem" }} />
              <StarIcon sx={{ color: "secondary.main", fontSize: "0.9rem" }} />
              <StarIcon sx={{ color: "secondary.main", fontSize: "0.9rem" }} />
              <StarIcon sx={{ color: "secondary.main", fontSize: "0.9rem" }} />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ opacity: 0.2, mb: 2 }} />

        <Grid container spacing={2}>
          {[
            { label: "Service", value: service?.name },
            {
              label: "Duration",
              value: service ? `${service.duration_minutes} min` : "—",
            },
            {
              label: "Date",
              value: startsAt ? format(startsAt, "MMMM d, yyyy") : "—",
            },
            { label: "Time", value: formatTime(slot?.start) || "—" },
          ].map(({ label, value }) => (
            <Grid key={label} size={isMobile ? 6 : 3}>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", ml: 1 }}
              >
                {label}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, ml: 1 }}>
                {value}
              </Typography>
            </Grid>
          ))}

          {(tenant?.address_line1 || tenant?.city) && (
            <Grid size={12}>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", ml: 1 }}
              >
                Location
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, ml: 1 }}>
                {[
                  tenant?.address_line1,
                  tenant?.address_line2,
                  tenant?.city,
                  tenant?.state,
                  tenant?.zip,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Payment method toggle */}
      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mb: 1.5, fontWeight: 500 }}
        >
          PAYMENT METHOD
        </Typography>
        <ToggleButtonGroup
          value={paymentMethod}
          exclusive
          onChange={(_, val) => val && onPaymentMethodChange(val)}
          sx={{ gap: 1 }}
        >
          {[
            {
              value: "card",
              icon: <CreditCardIcon sx={{ fontSize: "1.1rem" }} />,
              label: "Pay by Card",
            },
            {
              value: "cash",
              icon: <MoneyIcon sx={{ fontSize: "1.1rem" }} />,
              label: "Pay with Cash",
            },
          ].map(({ value, icon, label }) => (
            <ToggleButton
              key={value}
              value={value}
              sx={{
                border: "1px solid #d8d1c7 !important",
                borderRadius: "10px !important",
                px: 2.5,
                py: 1,
                textTransform: "none",
                fontSize: "0.875rem",
                gap: 1,
                "&.Mui-selected": {
                  backgroundColor: "#111008",
                  color: "white",
                  borderColor: "#111008 !important",
                },
              }}
            >
              {icon}
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Card input */}
      <Box className="calendar-shell" sx={{ backgroundColor: "white", mt: 2 }}>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mb: 1.5, fontWeight: 500 }}
        >
          {paymentMethod === "card"
            ? "CARD DETAILS"
            : "CARD ON FILE (for no-show protection)"}
        </Typography>

        {paymentMethod === "cash" && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              backgroundColor: "rgba(196,152,77,0.08)",
              color: "#111",
              border: "1px solid rgba(196,152,77,0.2)",
              "& .MuiAlert-icon": { color: "#C4984D" },
              fontSize: "0.82rem",
            }}
          >
            Your card won't be charged now. It's saved only to protect against
            no-shows or late cancellations. You'll pay $
            {servicePrice.toFixed(2)} cash at your appointment.
          </Alert>
        )}

        <Box
          sx={{
            border: "1px solid #d8d1c7",
            borderRadius: "10px",
            p: 2,
            backgroundColor: "#fafaf8",
            mb: 1,
          }}
        >
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </Box>

        {/* Tip selector — card only */}
        {paymentMethod === "card" && (
          <Box sx={{ mt: 2.5, mb: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 1.5,
                fontWeight: 500,
                fontSize: "0.75rem",
                letterSpacing: "0.06em",
              }}
            >
              ADD A TIP FOR YOUR BARBER (OPTIONAL)
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {TIP_PRESETS.map((preset) => (
                <Button
                  key={preset}
                  onClick={() => handleTipPreset(preset)}
                  sx={tipButtonSx(preset)}
                >
                  {preset === "none"
                    ? "No Tip"
                    : preset === "custom"
                      ? "Custom"
                      : `$${preset}`}
                </Button>
              ))}
            </Box>
            {tipPreset === "custom" && (
              <TextField
                size="small"
                placeholder="0.00"
                value={customTip}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9.]/g, "");
                  setCustomTip(val);
                  setTipAmount(parseFloat(val) || 0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        $
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mt: 1.5,
                  width: 130,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                    backgroundColor: "#fafaf8",
                    "& fieldset": { borderColor: "#d8d1c7" },
                    "&:hover fieldset": { borderColor: "#b0a898" },
                    "&.Mui-focused fieldset": { borderColor: "#111008" },
                  },
                }}
              />
            )}
          </Box>
        )}

        {/* Fee breakdown */}
        <Box sx={{ mt: 2.5 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Service price
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              ${servicePrice.toFixed(2)}
            </Typography>
          </Box>

          {paymentMethod === "card" && tipAmount > 0 && (
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Tip for barber
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "#C4984D" }}
              >
                +${tipAmount.toFixed(2)}
              </Typography>
            </Box>
          )}

          {paymentMethod === "card" && (
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Processing fee
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                ${processingFee.toFixed(2)}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 1, opacity: 0.3 }} />

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {paymentMethod === "card"
                ? "Total charged today"
                : "Due at appointment"}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {paymentMethod === "card"
                ? `$${totalCharge.toFixed(2)}`
                : `$${servicePrice.toFixed(2)} cash`}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Cancellation policy */}
      <Box
        sx={{
          mt: 2,
          p: 2.5,
          borderRadius: "10px",
          backgroundColor: "#fafaf8",
          border: "1px solid #e8e2d8",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            mb: 1,
            fontSize: "0.75rem",
            letterSpacing: "0.06em",
          }}
        >
          CANCELLATION & NO-SHOW POLICY
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", lineHeight: 1.7, fontSize: "0.82rem" }}
        >
          You have until{" "}
          <strong style={{ color: "#111" }}>
            {cancelDeadline
              ? formatDateTime(
                  cancelDeadline
                    .toISOString()
                    .replace("T", " ")
                    .replace(".000Z", ""),
                  tenant?.timezone,
                )
              : `${cancelWindowHours} hours before your appointment`}
          </strong>{" "}
          to cancel.{" "}
          {paymentMethod === "card" ? (
            <>
              If cancelled in time,{" "}
              <strong style={{ color: "#111" }}>
                ${(servicePrice + tipAmount).toFixed(2)}
              </strong>
              {tipAmount > 0 ? " (service + tip)" : ""} will be refunded. The
              processing fee is non-refundable.
            </>
          ) : (
            "If cancelled in time, no charge will be applied."
          )}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            lineHeight: 1.7,
            fontSize: "0.82rem",
            mt: 0.5,
          }}
        >
          {paymentMethod === "card" ? (
            "If you cancel after this deadline or do not show up, your payment is non-refundable."
          ) : (
            <>
              If you do not show up, a no-show fee of{" "}
              <strong style={{ color: "#111" }}>
                ${totalCharge.toFixed(2)}
              </strong>{" "}
              will be charged to your card on file.
            </>
          )}
        </Typography>
      </Box>

      {/* Notes */}
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 1,
            fontWeight: 500,
            fontSize: "0.75rem",
            letterSpacing: "0.06em",
          }}
        >
          NOTES FOR YOUR BARBER (OPTIONAL)
        </Typography>
        <TextField
          multiline
          rows={3}
          fullWidth
          placeholder="Any requests, style references, or things your barber should know..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              fontSize: "0.875rem",
              backgroundColor: "#fafaf8",
              "& fieldset": { borderColor: "#d8d1c7" },
              "&:hover fieldset": { borderColor: "#b0a898" },
              "&.Mui-focused fieldset": { borderColor: "#111008" },
            },
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Policy checkbox */}
      <Box
        sx={{
          mt: 2.5,
          p: 2,
          borderRadius: "10px",
          backgroundColor: agreed ? "rgba(196,152,77,0.06)" : "#fafaf8",
          border: "1px solid",
          borderColor: agreed ? "rgba(196,152,77,0.35)" : "#e8e2d8",
          transition: "all 0.2s ease",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              size="small"
              sx={{
                color: "#d8d1c7",
                "&.Mui-checked": { color: "#111008" },
                pt: 0,
                alignSelf: "flex-start",
                mt: "2px",
              }}
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.82rem",
                color: "text.secondary",
                lineHeight: 1.6,
              }}
            >
              I have read and agree to the{" "}
              <strong style={{ color: "#111008" }}>
                cancellation and no-show policy
              </strong>{" "}
              outlined above. I understand that{" "}
              {paymentMethod === "card" ? (
                <>
                  late cancellations and no-shows are{" "}
                  <strong style={{ color: "#111008" }}>non-refundable</strong>.
                </>
              ) : (
                <>
                  a fee of{" "}
                  <strong style={{ color: "#111008" }}>
                    ${totalCharge.toFixed(2)}
                  </strong>{" "}
                  may be charged for late cancellations or no-shows.
                </>
              )}
            </Typography>
          }
          alignItems="flex-start"
          sx={{ alignItems: "flex-start", m: 0 }}
        />
      </Box>

      {/* Submit */}
      <Button
        fullWidth
        onClick={handleSubmit}
        disabled={loading || !stripe || !agreed}
        sx={{
          mt: 2,
          backgroundColor: "#111008",
          color: "white",
          borderRadius: "10px",
          py: 1.5,
          fontSize: "0.95rem",
          fontWeight: 700,
          textTransform: "none",
          "&:hover": { backgroundColor: "#1e1c14" },
          "&:disabled": {
            backgroundColor: "rgba(0,0,0,0.2)",
            color: "rgba(0,0,0,0.4)",
          },
        }}
      >
        {loading ? (
          <CircularProgress size={18} sx={{ color: "white" }} />
        ) : paymentMethod === "card" ? (
          `Confirm & Pay $${totalCharge.toFixed(2)}`
        ) : (
          "Confirm Booking"
        )}
      </Button>

      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          textAlign: "center",
          mt: 1.5,
          fontSize: "0.75rem",
        }}
      >
        Secured by Stripe
      </Typography>
    </Box>
  );
};

const Confirmation = ({
  barber,
  service,
  slot,
  guestInfo,
  onSuccess,
  paymentMethod,
  onPaymentMethodChange,
  onTipAmountChange,
}) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm
        barber={barber}
        service={service}
        slot={slot}
        guestInfo={guestInfo}
        onSuccess={onSuccess}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={onPaymentMethodChange}
        onTipAmountChange={onTipAmountChange}
      />
    </Elements>
  );
};

export default Confirmation;
